import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import { useMembership } from '../../../hooks/MembershipProvider/MembershipProvider';
import styles from './CouponDetail.module.css';

interface CouponInfo {
  id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  usage_limit?: number;
  used_count: number;
  status: string;
  coupon_type: string;
  valid_from: string;
  valid_until: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

const CouponDetail: React.FC = () => {
  const { couponId } = useParams<{ couponId: string }>();
  const { getCoupon } = useApi();
  const { hasAdminPermission, hasSystemPermission } = useMembership();
  const navigate = useNavigate();
  
  const [coupon, setCoupon] = useState<CouponInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 쿠폰 상세 정보 조회
  const fetchCouponDetail = async () => {
    if (!couponId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getCoupon(couponId);
      setCoupon(response);
    } catch (err) {
      console.error('쿠폰 상세 조회 실패:', err);
      setError(err instanceof Error ? err.message : '쿠폰 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCouponDetail();
  }, [couponId]);

  // 상태 표시 함수
  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { text: string; className: string } } = {
      'active': { text: '활성', className: styles.statusActive },
      'inactive': { text: '비활성', className: styles.statusInactive },
      'expired': { text: '만료', className: styles.statusExpired },
      'used_up': { text: '소진', className: styles.statusUsedUp }
    };
    
    const statusInfo = statusMap[status] || { text: status, className: styles.statusDefault };
    return <span className={`${styles.statusBadge} ${statusInfo.className}`}>{statusInfo.text}</span>;
  };

  // 할인 타입 표시 함수
  const getDiscountTypeText = (couponType: string, value: number) => {
    switch (couponType) {
      case 'PERCENTAGE':
        return `${value}% 할인`;
      case 'FIXED_AMOUNT':
        return `${value.toLocaleString()}원 할인`;
      case 'GIFT_PLAN':
        return `${value.toLocaleString()}원 상품권`;
      case 'FREE_SHIPPING':
        return '무료 배송';
      default:
        return `${couponType}: ${value}`;
    }
  };

  // 권한 확인
  if (!hasAdminPermission() && !hasSystemPermission()) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지에 접근하려면 관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <p>쿠폰 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>오류가 발생했습니다</h2>
          <p>{error}</p>
          <button className={styles.btnPrimary} onClick={() => navigate('/admin/coupon')}>
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  if (!coupon) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>쿠폰을 찾을 수 없습니다</h2>
          <p>요청하신 쿠폰이 존재하지 않습니다.</p>
          <button className={styles.btnPrimary} onClick={() => navigate('/admin/coupon')}>
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            className={styles.btnSecondary}
            onClick={() => navigate('/admin/coupon')}
          >
            ← 목록으로
          </button>
          <h1 className="admin-page-title">쿠폰 상세 정보</h1>
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.btnPrimary}
            onClick={() => navigate(`/admin/coupon/${coupon.id}/edit`)}
          >
            수정
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {/* 기본 정보 */}
        <div className={styles.section}>
          <h2>기본 정보</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>쿠폰 코드</label>
              <div className={styles.couponCode}>{coupon.code}</div>
            </div>
            <div className={styles.infoItem}>
              <label>쿠폰명</label>
              <div className={styles.couponName}>{coupon.name}</div>
            </div>
            <div className={styles.infoItem}>
              <label>상태</label>
              <div>{getStatusBadge(coupon.status)}</div>
            </div>
            <div className={styles.infoItem}>
              <label>쿠폰 타입</label>
              <div className={styles.couponType}>{coupon.coupon_type}</div>
            </div>
          </div>
          {coupon.description && (
            <div className={styles.infoItem}>
              <label>설명</label>
              <div className={styles.description}>{coupon.description}</div>
            </div>
          )}
        </div>

        {/* 할인 정보 */}
        <div className={styles.section}>
          <h2>할인 정보</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>할인 타입</label>
              <div className={styles.discountValue}>
                {getDiscountTypeText(coupon.coupon_type, coupon.discount_value)}
              </div>
            </div>
            {coupon.min_order_amount && (
              <div className={styles.infoItem}>
                <label>최소 주문 금액</label>
                <div className={styles.amount}>{coupon.min_order_amount.toLocaleString()}원</div>
              </div>
            )}
            {coupon.max_discount_amount && (
              <div className={styles.infoItem}>
                <label>최대 할인 금액</label>
                <div className={styles.amount}>{coupon.max_discount_amount.toLocaleString()}원</div>
              </div>
            )}
          </div>
        </div>

        {/* 사용 제한 */}
        {coupon.usage_limit && (
          <div className={styles.section}>
            <h2>사용 제한</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>사용 제한</label>
                <div className={styles.usageLimit}>{coupon.usage_limit}회</div>
              </div>
            </div>
          </div>
        )}

        {/* 유효 기간 */}
        <div className={styles.section}>
          <h2>유효 기간</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>시작일</label>
              <div className={styles.date}>
                {new Date(coupon.valid_from).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
            <div className={styles.infoItem}>
              <label>만료일</label>
              <div className={styles.date}>
                {new Date(coupon.valid_until).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
            <div className={styles.infoItem}>
              <label>남은 기간</label>
              <div className={styles.remainingDays}>
                {new Date(coupon.valid_until) > new Date() 
                  ? `${Math.ceil((new Date(coupon.valid_until).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))}일`
                  : '만료됨'
                }
              </div>
            </div>
          </div>
        </div>

        {/* 생성/수정 정보 */}
        <div className={styles.section}>
          <h2>생성/수정 정보</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>생성일</label>
              <div className={styles.date}>
                {new Date(coupon.created_at).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            <div className={styles.infoItem}>
              <label>수정일</label>
              <div className={styles.date}>
                {new Date(coupon.updated_at).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            {coupon.created_by && (
              <div className={styles.infoItem}>
                <label>생성자</label>
                <div className={styles.userId}>{coupon.created_by}</div>
              </div>
            )}
            {coupon.updated_by && (
              <div className={styles.infoItem}>
                <label>수정자</label>
                <div className={styles.userId}>{coupon.updated_by}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponDetail;
